/**
 * Refdata cell math — pure, isomorphic helpers shared by the ingest
 * partitioner (scripts/data-ingest/refdata/), the server query engine
 * (src/server/refdata/queryEngine.ts), and tests.
 *
 * Cells are a fixed lat/lng grid. A cell id encodes its ORIGIN (south-west
 * corner) scaled by 100, plus the cell size, e.g.:
 *
 *   n3425w11950_p25  → origin 34.25°N 119.50°W, 0.25° cell
 *   s1425e17050_p50  → origin 14.25°S 170.50°E (American Samoa), 0.5° cell
 *
 * All four hemisphere prefixes are required: US territory spans negative
 * latitude (American Samoa) and positive longitude (Guam). Antimeridian
 * wraparound is deliberately unsupported — app policy (isUSCoordinate in
 * src/services/dataProviders/index.ts) already excludes Aleutian islands
 * west of 180°, so no query or ingest crosses ±180.
 */

export const SUPPORTED_CELL_SIZES_DEG = [0.1, 0.25, 0.5, 1] as const;

import type { RefdataBbox, RefdataGeometry, RefdataLatLng, RefdataPosition } from '../../types/refdata';

const KM_PER_DEG_LAT = 111.32;

function assertSupportedCellSize(cellSizeDeg: number): void {
  if (!(SUPPORTED_CELL_SIZES_DEG as readonly number[]).includes(cellSizeDeg)) {
    throw new Error(`Unsupported refdata cell size: ${cellSizeDeg}`);
  }
}

/** Scale an already-cell-aligned value by 100 into an exact integer (cell
 *  sizes are 0.1°+ so origins are centi-degree aligned). */
function centi(value: number): number {
  return Math.round(value * 100);
}

/**
 * Integer centi-degree origin of the cell containing `value`. The raw
 * coordinate is NEVER rounded (34.7999 must stay below 34.80); the tiny
 * epsilon only rescues exact boundaries that binary floats represent a hair
 * low (34.8 * 100 / 10 → 347.99999999999994 → 348).
 */
function floorToCellCenti(value: number, stepCenti: number): number {
  return Math.floor((value * 100) / stepCenti + 1e-9) * stepCenti;
}

function floorToCell(value: number, cellSizeDeg: number): number {
  return floorToCellCenti(value, centi(cellSizeDeg)) / 100;
}

function formatOrigin(originLat: number, originLng: number, cellSizeDeg: number): string {
  const latPrefix = originLat < 0 ? 's' : 'n';
  const lngPrefix = originLng < 0 ? 'w' : 'e';
  const latC = Math.abs(centi(originLat)).toString().padStart(4, '0');
  const lngC = Math.abs(centi(originLng)).toString().padStart(5, '0');
  return `${latPrefix}${latC}${lngPrefix}${lngC}_p${centi(cellSizeDeg)}`;
}

/** Cell id containing the given point. */
export function cellId(lat: number, lng: number, cellSizeDeg: number): string {
  assertSupportedCellSize(cellSizeDeg);
  return formatOrigin(floorToCell(lat, cellSizeDeg), floorToCell(lng, cellSizeDeg), cellSizeDeg);
}

const CELL_ID_RE = /^([ns])(\d{4})([ew])(\d{5})_p(\d{2,3})$/;

/** Parse a cell id back into its bounding box. Throws on malformed ids. */
export function bboxForCell(id: string): RefdataBbox {
  const m = CELL_ID_RE.exec(id);
  if (!m) throw new Error(`Malformed refdata cell id: ${id}`);
  const originLat = (m[1] === 's' ? -1 : 1) * Number(m[2]) / 100;
  const originLng = (m[3] === 'w' ? -1 : 1) * Number(m[4]) / 100;
  const size = Number(m[5]) / 100;
  assertSupportedCellSize(size);
  return { minLat: originLat, minLng: originLng, maxLat: originLat + size, maxLng: originLng + size };
}

/** All cell ids intersecting a bbox (inclusive of edge-touching cells). */
export function cellsForBbox(bbox: RefdataBbox, cellSizeDeg: number): string[] {
  assertSupportedCellSize(cellSizeDeg);
  const stepCenti = centi(cellSizeDeg);
  const minLatC = floorToCellCenti(bbox.minLat, stepCenti);
  const maxLatC = floorToCellCenti(bbox.maxLat, stepCenti);
  const minLngC = floorToCellCenti(bbox.minLng, stepCenti);
  const maxLngC = floorToCellCenti(bbox.maxLng, stepCenti);
  const ids: string[] = [];
  for (let latC = minLatC; latC <= maxLatC; latC += stepCenti) {
    for (let lngC = minLngC; lngC <= maxLngC; lngC += stepCenti) {
      ids.push(formatOrigin(latC / 100, lngC / 100, cellSizeDeg));
    }
  }
  return ids;
}

/**
 * Cell ids at Chebyshev ring `ring` around the cell containing (lat, lng).
 * ring 0 = the containing cell alone; ring 1 = the 8 surrounding cells; etc.
 * Used by nearest-N queries to expand the search outward until the ring's
 * closest possible feature is farther than the current Nth-best hit.
 */
export function cellRing(lat: number, lng: number, cellSizeDeg: number, ring: number): string[] {
  assertSupportedCellSize(cellSizeDeg);
  const stepCenti = centi(cellSizeDeg);
  const centerLatC = floorToCellCenti(lat, stepCenti);
  const centerLngC = floorToCellCenti(lng, stepCenti);
  if (ring === 0) return [formatOrigin(centerLatC / 100, centerLngC / 100, cellSizeDeg)];
  const ids: string[] = [];
  for (let dy = -ring; dy <= ring; dy++) {
    for (let dx = -ring; dx <= ring; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
      const latC = centerLatC + dy * stepCenti;
      const lngC = centerLngC + dx * stepCenti;
      if (latC < -9000 || latC + stepCenti > 9000) continue;
      if (lngC < -18000 || lngC + stepCenti > 18000) continue;
      ids.push(formatOrigin(latC / 100, lngC / 100, cellSizeDeg));
    }
  }
  return ids;
}

/**
 * Minimum possible distance (km) from a point to any feature in ring `ring`:
 * (ring - 1) whole cells of separation is the floor once ring ≥ 2; rings 0/1
 * can always contain features arbitrarily close to the point.
 */
export function ringMinDistanceKm(lat: number, cellSizeDeg: number, ring: number): number {
  if (ring <= 1) return 0;
  const latKm = (ring - 1) * cellSizeDeg * KM_PER_DEG_LAT;
  const lngKm = (ring - 1) * cellSizeDeg * KM_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
  return Math.min(latKm, lngKm);
}

// ---------------------------------------------------------------------------
// Geometry predicates & distances (local equirectangular projection — exact
// enough at the ≤100 km scales of refdata queries; error < 0.5% there).
// ---------------------------------------------------------------------------

export function bboxesIntersect(a: RefdataBbox, b: RefdataBbox): boolean {
  return a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLng <= b.maxLng && a.maxLng >= b.minLng;
}

export function geometryBbox(geom: RefdataGeometry): RefdataBbox {
  let minLat = Infinity, minLng = Infinity, maxLat = -Infinity, maxLng = -Infinity;
  const visit = (pos: RefdataPosition): void => {
    const [lng, lat] = pos;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  };
  forEachPosition(geom, visit);
  return { minLat, minLng, maxLat, maxLng };
}

export function forEachPosition(geom: RefdataGeometry, visit: (pos: RefdataPosition) => void): void {
  switch (geom.type) {
    case 'Point': visit(geom.coordinates); break;
    case 'MultiPoint':
    case 'LineString': geom.coordinates.forEach(visit); break;
    case 'MultiLineString':
    case 'Polygon': geom.coordinates.forEach(ring => ring.forEach(visit)); break;
    case 'MultiPolygon': geom.coordinates.forEach(poly => poly.forEach(ring => ring.forEach(visit))); break;
  }
}

/** Local projection of a position to km, centred on `origin`. */
function toLocalKm(pos: RefdataPosition, origin: RefdataLatLng): { x: number; y: number } {
  const cosLat = Math.cos((origin.lat * Math.PI) / 180);
  return {
    x: (pos[0] - origin.lng) * KM_PER_DEG_LAT * cosLat,
    y: (pos[1] - origin.lat) * KM_PER_DEG_LAT,
  };
}

function pointToSegmentKmLocal(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const abx = b.x - a.x, aby = b.y - a.y;
  const lengthSq = abx * abx + aby * aby;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * abx), p.y - (a.y + t * aby));
}

function minDistanceToLineKm(point: RefdataLatLng, line: RefdataPosition[]): number {
  const p = { x: 0, y: 0 };
  let min = Infinity;
  for (let i = 0; i + 1 < line.length; i++) {
    const d = pointToSegmentKmLocal(p, toLocalKm(line[i], point), toLocalKm(line[i + 1], point));
    if (d < min) min = d;
  }
  if (line.length === 1) {
    const only = toLocalKm(line[0], point);
    min = Math.hypot(only.x, only.y);
  }
  return min;
}

/**
 * Boundary-inclusive even-odd point-in-polygon over one or more rings
 * (outer + holes together — even-odd handles holes without ring-role
 * bookkeeping). Ray-cast in raw lng/lat: containment is affine-enough at
 * these latitudes and poles/antimeridian are excluded by policy.
 * Port of pointInLocalPolygon (src/services/dataProviders/topography/terrainGrid.ts:92).
 */
export function pointInRings(point: RefdataLatLng, rings: RefdataPosition[][]): boolean {
  const px = point.lng, py = point.lat;
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [ax, ay] = ring[j];
      const [bx, by] = ring[i];
      const lenSq = (bx - ax) ** 2 + (by - ay) ** 2;
      // GeoJSON rings repeat the first vertex as the last — that closing
      // edge is zero-length, and a degenerate segment would classify EVERY
      // point as "on the boundary". Skip it.
      if (lenSq < 1e-24) continue;
      // Boundary-inclusive: on-segment counts as inside.
      const cross = (py - ay) * (bx - ax) - (px - ax) * (by - ay);
      if (Math.abs(cross) < 1e-12) {
        const dot = (px - ax) * (bx - ax) + (py - ay) * (by - ay);
        if (dot >= -1e-12 && dot <= lenSq + 1e-12) return true;
      }
      const intersects = (ay > py) !== (by > py) && px < ((bx - ax) * (py - ay)) / (by - ay) + ax;
      if (intersects) inside = !inside;
    }
  }
  return inside;
}

export function pointInGeometry(point: RefdataLatLng, geom: RefdataGeometry): boolean {
  if (geom.type === 'Polygon') return pointInRings(point, geom.coordinates);
  if (geom.type === 'MultiPolygon') return geom.coordinates.some(poly => pointInRings(point, poly));
  return false;
}

/**
 * Distance in km from a point to a geometry: 0 when inside a polygon,
 * else the minimum distance to its vertices/edges.
 */
export function pointToGeometryKm(point: RefdataLatLng, geom: RefdataGeometry): number {
  switch (geom.type) {
    case 'Point':
      return minDistanceToLineKm(point, [geom.coordinates]);
    case 'MultiPoint': {
      let min = Infinity;
      for (const c of geom.coordinates) min = Math.min(min, minDistanceToLineKm(point, [c]));
      return min;
    }
    case 'LineString':
      return minDistanceToLineKm(point, geom.coordinates);
    case 'MultiLineString': {
      let min = Infinity;
      for (const line of geom.coordinates) min = Math.min(min, minDistanceToLineKm(point, line));
      return min;
    }
    case 'Polygon': {
      if (pointInRings(point, geom.coordinates)) return 0;
      let min = Infinity;
      for (const ring of geom.coordinates) min = Math.min(min, minDistanceToLineKm(point, ring));
      return min;
    }
    case 'MultiPolygon': {
      let min = Infinity;
      for (const poly of geom.coordinates) {
        if (pointInRings(point, poly)) return 0;
        for (const ring of poly) min = Math.min(min, minDistanceToLineKm(point, ring));
      }
      return min;
    }
  }
}

/** Bbox around a point with a radius in km (lng span widened by 1/cos(lat)). */
export function bboxAroundPoint(point: RefdataLatLng, radiusKm: number): RefdataBbox {
  const dLat = radiusKm / KM_PER_DEG_LAT;
  const cosLat = Math.max(0.05, Math.cos((point.lat * Math.PI) / 180));
  const dLng = radiusKm / (KM_PER_DEG_LAT * cosLat);
  return {
    minLat: point.lat - dLat,
    minLng: point.lng - dLng,
    maxLat: point.lat + dLat,
    maxLng: point.lng + dLng,
  };
}

/** Quantize a coordinate to 6 decimals (~0.11 m) — applied BEFORE cell
 *  clipping at ingest so fragments in adjacent cells share edge vertices. */
export function quantize6(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
